import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import type { UserRoleRequest } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useSlug } from "@/hooks/useSlug";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { permissionMutations } from "@/queries/permissions/mutations";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useServicePartnersQueries } from "@/queries/service-partners/queryOptions";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { RoleForm, type RoleFormProps } from "../components/RoleForm";
import { compileStructure, getDefaultFormValues } from "../components/roleFormManager";

export function EditRolePage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const roleId = id ?? "";

  const communityGroupQueries = useCommunityGroupQueries();
  const permissionQueries = usePermissionQueries();
  const documentQueries = useDocumentQueries();
  const ticketCategoryQueries = useTicketCategoryQueries();
  const servicePartnersQueries = useServicePartnersQueries();

  const updateRoleMutation = permissionMutations.useUpdateRole();

  const queryArgs = {
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;
  const {
    data: communityGroups = [],
    isFetching: isGroupsFetching,
    error: groupsError,
  } = useQuery({
    ...communityGroupQueries.list(queryArgs),
    select: (data) =>
      commonAPIDataSelector(data)
        .items.filter((group) => !group.isResidentGroup)
        .toSorted((a, b) => a.name.localeCompare(b.name)),
  });
  const {
    data: roleDetails,
    isFetching: isRoleDetailsFetching,
    error: roleDetailsError,
  } = useQuery({
    ...permissionQueries.roleDetails(roleId),
  });
  const {
    data: folders = [],
    isFetching: isFoldersFetching,
    error: foldersError,
  } = useQuery({
    ...documentQueries.listRoots(),
    select: (data) => commonAPIDataSelector(data).rootFolders.toSorted((a, b) => a.name.localeCompare(b.name)),
  });
  const {
    data: ticketCategories = [],
    isFetching: isTicketCategoriesFetching,
    error: ticketCategoriesError,
  } = useQuery({
    ...ticketCategoryQueries.list(),
    select: (data) => commonAPIDataSelector(data).items,
  });
  const {
    data: servicePartners = [],
    isFetching: isServicePartnersFetching,
    error: servicePartnersError,
  } = useQuery({
    ...servicePartnersQueries.list(),
    select: (data) => commonAPIDataSelector(data).items.toSorted((a, b) => a.title.localeCompare(b.title)),
  });
  const isPageLoading =
    isGroupsFetching ||
    isRoleDetailsFetching ||
    isFoldersFetching ||
    isTicketCategoriesFetching ||
    isServicePartnersFetching;
  const defaultFormValues = useMemo(() => {
    if (isPageLoading) {
      return undefined;
    }

    return getDefaultFormValues(
      {
        communityGroups,
        ticketCategories: ticketCategories,
        servicePartners,
        folders,
      },
      roleDetails,
    );
  }, [isPageLoading, communityGroups, ticketCategories, servicePartners, folders, roleDetails]);

  const onSubmit: RoleFormProps["onSubmit"] = (data) => {
    const payload: UserRoleRequest = {
      ...data,
      permissions: {
        ...data.permissions,
        communityGroups: Object.values(data.permissions.communityGroups),
        repairCategories: Object.values(data.permissions.repairCategories),
        servicePages: Object.values(data.permissions.servicePages),
        folders: Object.values(data.permissions.folders),
      },
    };

    void updateRoleMutation.mutateAsync(
      { id: roleId, payload },
      {
        onSuccess: () => {
          void navigate(routes.roles.list({ slug }));
        },
      },
    );
  };

  const structure = useMemo(
    () => compileStructure(t, communityGroups, ticketCategories, servicePartners, folders),
    [communityGroups, folders, ticketCategories, servicePartners, t],
  );

  const error = roleDetailsError || groupsError || ticketCategoriesError || servicePartnersError || foldersError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (roleDetails?.locked) {
    return (
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("model.role.action.edit.is-locked")}
        breadcrumbs={<Breadcrumbs pages={[{ name: t("model.role.action.edit.is-locked") }]} />}
      />
    );
  }

  return isPageLoading || defaultFormValues == null ? (
    <FullSizeLoader withPadding />
  ) : (
    <RoleForm
      onSubmit={onSubmit}
      defaultFormValues={defaultFormValues}
      isSubmitting={updateRoleMutation.isPending}
      structure={structure}
      isDuplicate={false}
    />
  );
}
